/**
 * Integration store — Composio v3.
 *
 * Owns the toolkit catalogue, the user's connection map, and the
 * mid-OAuth "pending" state. The OAuth window opens in the user's
 * default browser via ``openExternalUrl``; completion is signalled to
 * the desktop through the ``integration.connection_changed`` WebSocket
 * event, which ``handleWebSocketEvent`` consumes. The status check is a
 * fallback for the rare case where the WebSocket is briefly down.
 * Components subscribe via the standard Zustand selector pattern.
 */

import { create } from "zustand";
import {
  disconnectToolkit,
  getConnectionStatus,
  initiateConnection,
  listConnections,
  listToolkits,
  type IntegrationConnection,
  type Toolkit,
} from "@/api/integrations";
import {
  findToolkit,
  normaliseToolkitPage,
  normalizeToolkitSlug,
  toolkitDisplayName,
} from "@/lib/composio";
import { openExternalUrl } from "@/lib/open-external-url";
import { toastAppConnected, toastAppError } from "@/stores/integration-notices";
import { translate } from "@/i18n";
import { storesNoticesMessages } from "@/i18n/messages/stores-notices";

/* ---------- Constants ---------- */

let accountGeneration = 0;
let catalogGeneration = 0;
/** Pause between status checks while a browser sign-in is open (ms). */
const POLL_INTERVAL_MS = 3000;
/** Hard cap on a single pending browser sign-in (ms). */
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

/** The next status check of each pending sign-in. */
const statusChecks = new Map<string, ReturnType<typeof setTimeout>>();
/** App lookups in flight, so one name is fetched once. */
const toolkitLookups = new Map<string, Promise<Toolkit | undefined>>();

/* ---------- Types ---------- */

/** A browser sign-in, from the Connect press until it settles or ends.
 *  Its status checks stop as soon as it is no longer the current entry. */
interface PendingAuth {
  startedAt: number;
}

/** Event shape from Django's ``integration.connection_changed``. */
export interface ConnectionChangedEvent {
  toolkit_slug: string;
  action: "connected" | "disconnected" | "expired";
  status?: string;
  connection_id?: string;
}

interface IntegrationState {
  /** Composio toolkit catalogue — concat'd across loaded pages. */
  toolkits: Toolkit[];
  /** Total number of toolkits Composio reports for the current search. */
  toolkitsTotal: number;
  /** Last loaded page (1-indexed). 0 means nothing loaded yet. */
  toolkitsPage: number;
  /** True while another page can be fetched. */
  toolkitsHasMore: boolean;
  /** Search term backing the current ``toolkits`` array. */
  toolkitsSearch: string;
  /** True while ``loadMoreToolkits`` is in flight. */
  toolkitsLoadingMore: boolean;
  /** Apps looked up one by one, keyed by slug, outside the catalogue page. */
  resolvedToolkits: Map<string, Toolkit>;
  /** User's connections keyed by ``toolkit_slug``. */
  connections: Map<string, IntegrationConnection>;
  /** Toolkits mid-OAuth, keyed by ``toolkit_slug``. */
  pendingAuth: Map<string, PendingAuth>;
  /** True while the initial catalogue/connections load is in flight. */
  loading: boolean;
  /** Last error from a store action (toolkit load, initiate, etc.). */
  error: string | null;

  actions: {
    /** Reset to page 1 of the catalogue for the given search term. */
    loadToolkits: (search?: string) => Promise<void>;
    /** Append the next page using the active search term. No-op when no more. */
    loadMoreToolkits: () => Promise<void>;
    /** The app's catalogue entry, looked up once when no loaded page has it. */
    resolveToolkit: (toolkitSlug: string) => Promise<Toolkit | undefined>;
    refreshConnections: () => Promise<void>;
    /** Start a browser sign-in; a no-op while one is pending for the app. */
    initiateConnection: (toolkitSlug: string) => Promise<void>;
    disconnect: (toolkitSlug: string) => Promise<void>;
    pollConnectionStatus: (
      toolkitSlug: string,
    ) => Promise<IntegrationConnection | null>;
    handleWebSocketEvent: (event: ConnectionChangedEvent) => void;
    cancelAllPending: () => void;
    /** Hard reset — used by sign-out. */
    reset: () => void;
  };
}

/* ---------- Helpers ---------- */

/** The app's catalogue entry from a loaded page or an earlier lookup. */
export function selectToolkit(
  state: Pick<IntegrationState, "toolkits" | "resolvedToolkits">,
  toolkitSlug: string,
): Toolkit | undefined {
  return (
    findToolkit(toolkitSlug, state.toolkits) ??
    state.resolvedToolkits.get(normalizeToolkitSlug(toolkitSlug))
  );
}

function setConnection(
  state: IntegrationState,
  conn: IntegrationConnection,
): Map<string, IntegrationConnection> {
  const next = new Map(state.connections);
  next.set(conn.toolkit_slug, conn);
  return next;
}

function removeConnection(
  state: IntegrationState,
  slug: string,
): Map<string, IntegrationConnection> {
  const next = new Map(state.connections);
  next.delete(slug);
  return next;
}

function clearPending(
  state: IntegrationState,
  slug: string,
): Map<string, PendingAuth> {
  clearTimeout(statusChecks.get(slug));
  statusChecks.delete(slug);
  // The OAuth window is the user's browser's; its callback page closes it.
  const next = new Map(state.pendingAuth);
  next.delete(slug);
  return next;
}

function isCurrentSignIn(slug: string, entry: PendingAuth): boolean {
  return useIntegrationStore.getState().pendingAuth.get(slug) === entry;
}

/** Check again after a pause, only while ``entry`` is still the sign-in. */
function scheduleStatusCheck(slug: string, entry: PendingAuth): void {
  clearTimeout(statusChecks.get(slug));
  statusChecks.set(
    slug,
    setTimeout(() => void checkSignIn(slug, entry), POLL_INTERVAL_MS),
  );
}

async function checkSignIn(slug: string, entry: PendingAuth): Promise<void> {
  if (!isCurrentSignIn(slug, entry)) return;
  const { actions } = useIntegrationStore.getState();
  const timedOut = Date.now() - entry.startedAt >= POLL_TIMEOUT_MS;
  // A settled answer ends the sign-in, so a current entry is still open.
  await actions.pollConnectionStatus(slug);
  if (!isCurrentSignIn(slug, entry)) return;
  if (!timedOut) return scheduleStatusCheck(slug, entry);
  // Give up on the server too, so every window offers Connect again.
  toastError(slug, translate(storesNoticesMessages, "connectionTimedOut"));
  await actions.disconnect(slug);
}

function appName(slug: string): string {
  return toolkitDisplayName(
    slug,
    selectToolkit(useIntegrationStore.getState(), slug),
  );
}

const toastError = (slug: string, reason: string) =>
  toastAppError(slug, appName(slug), reason);

/* ---------- Store ---------- */

export const useIntegrationStore = create<IntegrationState>((set, get) => ({
  toolkits: [],
  toolkitsTotal: 0,
  toolkitsPage: 0,
  toolkitsHasMore: false,
  toolkitsSearch: "",
  toolkitsLoadingMore: false,
  resolvedToolkits: new Map(),
  connections: new Map(),
  pendingAuth: new Map(),
  loading: false,
  error: null,

  actions: {
    async loadToolkits(search) {
      const generation = accountGeneration;
      const request = ++catalogGeneration;
      const searchTerm = search ?? "";
      set({ loading: true, error: null });
      try {
        const [rawPage, connections] = await Promise.all([
          listToolkits({ search, page: 1 }),
          listConnections(),
        ]);
        if (generation !== accountGeneration || request !== catalogGeneration)
          return;
        const page = normaliseToolkitPage(rawPage);
        const map = new Map<string, IntegrationConnection>();
        for (const c of connections) map.set(c.toolkit_slug, c);
        set({
          toolkits: page.items,
          toolkitsTotal: page.total,
          toolkitsPage: page.page,
          toolkitsHasMore: page.has_more,
          toolkitsSearch: searchTerm,
          connections: map,
          loading: false,
        });
      } catch (err) {
        if (generation !== accountGeneration || request !== catalogGeneration)
          return;
        set({
          loading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },

    async loadMoreToolkits() {
      const generation = accountGeneration;
      const state = get();
      // Guard against concurrent calls, missing first-page, and exhausted catalogues.
      if (
        state.toolkitsLoadingMore ||
        state.loading ||
        !state.toolkitsHasMore ||
        state.toolkitsPage === 0
      ) {
        return;
      }
      const nextPage = state.toolkitsPage + 1;
      const search = state.toolkitsSearch;
      const expectedPriorPage = state.toolkitsPage;
      // A changed search or a reset mid-fetch drops the stale page on land;
      // toolkitsPage catches a reset that leaves the search "" both times.
      set({ toolkitsLoadingMore: true });
      try {
        const result = normaliseToolkitPage(
          await listToolkits({
            search: search || undefined,
            page: nextPage,
          }),
          { page: nextPage, total: state.toolkitsTotal },
        );
        if (generation !== accountGeneration) return;
        const current = get();
        if (
          current.toolkitsSearch !== search ||
          current.toolkitsPage !== expectedPriorPage
        ) {
          // Search changed or store was reset since we started; discard this page.
          set({ toolkitsLoadingMore: false });
          return;
        }
        // Dedupe in case the user's catalog overlaps with what's in memory.
        const seen = new Set(current.toolkits.map((t) => t.name));
        const fresh = result.items.filter((t) => !seen.has(t.name));
        set({
          toolkits: [...current.toolkits, ...fresh],
          toolkitsTotal: result.total,
          toolkitsPage: result.page,
          toolkitsHasMore: result.has_more,
          toolkitsLoadingMore: false,
        });
      } catch (err) {
        if (generation !== accountGeneration) return;
        set({
          toolkitsLoadingMore: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },

    resolveToolkit(toolkitSlug) {
      const slug = normalizeToolkitSlug(toolkitSlug);
      const known = selectToolkit(get(), slug);
      if (known || !slug) return Promise.resolve(known);
      const inFlight = toolkitLookups.get(slug);
      if (inFlight) return inFlight;
      const generation = accountGeneration;
      // The server searches its cached catalogue, so this is one cheap read.
      const lookup = listToolkits({ search: slug })
        .then((page) => findToolkit(slug, normaliseToolkitPage(page).items))
        .catch(() => undefined)
        .then((toolkit) => {
          if (toolkitLookups.get(slug) === lookup) toolkitLookups.delete(slug);
          if (toolkit && generation === accountGeneration)
            set((state) => ({
              resolvedToolkits: new Map(state.resolvedToolkits).set(
                slug,
                toolkit,
              ),
            }));
          return toolkit;
        });
      toolkitLookups.set(slug, lookup);
      return lookup;
    },

    async refreshConnections() {
      const generation = accountGeneration;
      try {
        const connections = await listConnections();
        if (generation !== accountGeneration) return;
        const map = new Map<string, IntegrationConnection>();
        for (const c of connections) map.set(c.toolkit_slug, c);
        set({ connections: map });
      } catch (err) {
        if (generation !== accountGeneration) return;
        set({ error: err instanceof Error ? err.message : String(err) });
      }
    },

    async initiateConnection(toolkitSlug) {
      // One browser sign-in per app: repeated presses must not open more.
      if (get().pendingAuth.has(toolkitSlug)) return;
      const generation = accountGeneration;
      const entry: PendingAuth = { startedAt: Date.now() };
      set((state) => ({
        pendingAuth: new Map(state.pendingAuth).set(toolkitSlug, entry),
      }));
      void get().actions.resolveToolkit(toolkitSlug);
      try {
        const result = await initiateConnection(toolkitSlug);
        if (!isCurrentSignIn(toolkitSlug, entry)) {
          // Cancelled while the server answered: its cancel may have run
          // before this sign-in existed there, so end it too. A newer
          // sign-in replaces it on the server by itself.
          const cancelled =
            generation === accountGeneration &&
            !get().pendingAuth.has(toolkitSlug);
          if (cancelled && result.redirect_url)
            void disconnectToolkit(toolkitSlug).catch(() => undefined);
          return;
        }
        set((state) => ({
          connections: setConnection(state, {
            id: result.connection_id,
            toolkit_slug: toolkitSlug,
            status: result.status,
            connected_at: null,
            created_at: new Date().toISOString(),
          }),
        }));

        // An empty redirect_url means the app is already active.
        if (!result.redirect_url) {
          set((state) => ({ pendingAuth: clearPending(state, toolkitSlug) }));
          return;
        }

        // OAuth runs in the user's default browser, not awaited: the status
        // checks cover success and an early-dismissed window. A failed open
        // keeps the sign-in pending; it may have finished in another window.
        void openExternalUrl(result.redirect_url).catch((err) => {
          const reason = err instanceof Error ? err.message : String(err);
          toastError(
            toolkitSlug,
            translate(storesNoticesMessages, "oauthWindowFailed", { reason }),
          );
        });
        scheduleStatusCheck(toolkitSlug, entry);
      } catch (err) {
        // A cancelled or signed-out sign-in has nothing left to report.
        if (!isCurrentSignIn(toolkitSlug, entry)) return;
        set((state) => ({ pendingAuth: clearPending(state, toolkitSlug) }));
        const reason = err instanceof Error ? err.message : String(err);
        set({ error: reason });
        toastError(toolkitSlug, reason);
      }
    },

    async disconnect(toolkitSlug) {
      const generation = accountGeneration;
      // Cancelling ends the sign-in at once, whatever the server answers.
      set((state) => ({ pendingAuth: clearPending(state, toolkitSlug) }));
      try {
        await disconnectToolkit(toolkitSlug);
        if (generation !== accountGeneration) return;
        set((state) => ({
          connections: removeConnection(state, toolkitSlug),
        }));
      } catch (err) {
        if (generation !== accountGeneration) return;
        const reason = err instanceof Error ? err.message : String(err);
        set({ error: reason });
        toastError(toolkitSlug, reason);
      }
    },

    async pollConnectionStatus(toolkitSlug) {
      const generation = accountGeneration;
      const signIn = get().pendingAuth.get(toolkitSlug);
      try {
        const connection = await getConnectionStatus(toolkitSlug);
        if (generation !== accountGeneration) return null;
        // The sign-in this read served ended meanwhile: cancelled, timed
        // out or settled by another read. Its answer must not undo that.
        if (signIn && get().pendingAuth.get(toolkitSlug) !== signIn)
          return null;
        // Toasts fire only on a real transition: a check landing after the
        // WebSocket event already settled the row stays silent.
        const prevStatus = get().connections.get(toolkitSlug)?.status;
        set((state) => ({ connections: setConnection(state, connection) }));
        const terminal =
          connection.status === "active" ||
          connection.status === "failed" ||
          connection.status === "expired";
        // A settled row won't change again: end the sign-in and its checks.
        if (terminal)
          set((state) => ({ pendingAuth: clearPending(state, toolkitSlug) }));
        if (prevStatus === connection.status) return connection;
        if (connection.status === "active") {
          toastAppConnected(toolkitSlug, appName(toolkitSlug));
        } else if (
          connection.status === "failed" ||
          connection.status === "expired"
        ) {
          toastError(
            toolkitSlug,
            connection.status === "expired"
              ? translate(storesNoticesMessages, "connectionExpired")
              : translate(storesNoticesMessages, "connectionFailed"),
          );
        }
        return connection;
      } catch (err) {
        if (generation !== accountGeneration) return null;
        // Network blip or 404 (no row yet): keep checking until the cap.
        if (err instanceof Error) set({ error: err.message });
        return null;
      }
    },

    handleWebSocketEvent(event) {
      const slug = event.toolkit_slug;
      if (!slug) return;
      if (event.action === "disconnected") {
        // The row is gone, so a status read would 404: trust the broadcast.
        set((state) => ({
          connections: removeConnection(state, slug),
          pendingAuth: clearPending(state, slug),
        }));
        return;
      }
      // The broadcast says *that* something changed; read the row once.
      void get().actions.pollConnectionStatus(slug);
    },

    cancelAllPending() {
      for (const check of statusChecks.values()) clearTimeout(check);
      statusChecks.clear();
      set({ pendingAuth: new Map() });
    },

    reset() {
      accountGeneration++;
      catalogGeneration++;
      get().actions.cancelAllPending();
      toolkitLookups.clear();
      set({
        toolkits: [],
        toolkitsTotal: 0,
        toolkitsPage: 0,
        toolkitsHasMore: false,
        toolkitsSearch: "",
        toolkitsLoadingMore: false,
        resolvedToolkits: new Map(),
        connections: new Map(),
        loading: false,
        error: null,
      });
    },
  },
}));
